import { DateTime } from "luxon"
import { DeliveryTimesField, DeliveryWithCart, DeliveryTimeDay } from "../../form/formTypes"
import { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"
import { CreateUpdateDeliveryFormValues } from "./CreateUpdateDeliveryTimePopover"

export const MAX_DELIVERIES = 5

export const prepareInitialValues = (
    dateTimeFactory: DateTimeFactory,
    eventStartTime: DateTime | null,
    openingHours: DeliveryTimeDay[],
    deliveries?: DeliveryTimesField,
): CreateUpdateDeliveryFormValues => {
    const deliveryDate = deliveries?.deliveryDate ? dateTimeFactory.fromISO(deliveries.deliveryDate) : eventStartTime

    const limits = deliveryDate ? getOpeningLimitsForDate(deliveryDate, openingHours) : null

    if (!deliveries || deliveries.deliveryTimes.length === 0) {
        let initialTime: DateTime | null = eventStartTime

        if (eventStartTime && deliveryDate && limits) {
            const minBound = deliveryDate.set({ hour: limits.minTime.hour, minute: limits.minTime.minute })
            const maxBound = deliveryDate.set({ hour: limits.maxTime.hour, minute: limits.maxTime.minute })
            const isInsideBounds = eventStartTime >= minBound && eventStartTime <= maxBound

            initialTime = isInsideBounds ? eventStartTime : null
        }

        return {
            deliveryDate,
            deliveryTimes: [{ time: initialTime, orderLines: [] }],
        }
    }

    const baseDateISO = deliveries?.deliveryDate
        ? deliveries.deliveryDate // "yyyy-MM-dd"
        : deliveryDate
        ? deliveryDate.toISODate()! //  "yyyy-MM-dd"
        : dateTimeFactory.now().toISODate()!

    const deliveryTimes = deliveries.deliveryTimes.map((delivery) => {
        const timeString = delivery.time ? `${baseDateISO}T${delivery.time}` : null

        return {
            time: timeString ? dateTimeFactory.fromISO(timeString) : null,
            orderLines: delivery.orderLines || [],
        }
    })

    return { deliveryDate, deliveryTimes }
}

type ViewPickerType = "hours" | "minutes" | "seconds"

// export function generateDisabledTimeSlots(
//     otherTimes: string[], // "HH:mm"
// ): (value: DateTime, view: ViewPickerType) => boolean {
//     return (value: DateTime, view: ViewPickerType): boolean => {
//         if (!value?.isValid || view !== "minutes") return false
//         const currentTimeStr = value.toFormat("HH:mm")
//         return otherTimes.includes(currentTimeStr)
//     }
// }

export function generateDisabledTimeSlots(otherTimes: Array<string | null>) {
    const disabled = new Set(otherTimes.filter((t): t is string => Boolean(t)))

    return (value: DateTime, view: ViewPickerType): boolean =>
        view === "minutes" && value?.isValid && disabled.has(value.toFormat("HH:mm"))
}

export function getOpeningLimitsForDate(date: DateTime, openingHours: DeliveryTimeDay[]) {
    if (!openingHours) return null

    const targetWeekday = date.weekday // 1–7
    const matched = openingHours.find((o) => o.dayOfWeek === targetWeekday)

    if (!matched) return null

    return {
        minTime: DateTime.fromFormat(matched.openingTime, "HH:mm").set({
            year: date.year,
            month: date.month,
            day: date.day,
        }),
        maxTime: DateTime.fromFormat(matched.closingTime, "HH:mm").set({
            year: date.year,
            month: date.month,
            day: date.day,
        }),
    }
}

export function sortedDeliveryTimes(times: DeliveryWithCart[]): DeliveryWithCart[] {
    return times.sort((a, b) => (a.time ?? "").localeCompare(b.time ?? ""))
}

export type DeliveryDateValidationResult = Readonly<{
    isAllowedWeekday: boolean
    isNotClosed: boolean
    isValidDeliveryDate: boolean
}>
export function validateDeliveryDate(
    date: DateTime | null,
    allowedWeekdays: number[],
    closingDates: string[],
): DeliveryDateValidationResult {
    if (!date) {
        return { isAllowedWeekday: true, isNotClosed: true, isValidDeliveryDate: true }
    }

    const isAllowedWeekday = allowedWeekdays.includes(date.weekday)
    const formatted = date.toFormat("yyyy.MM.dd")
    const isNotClosed = !closingDates.includes(formatted)

    return {
        isAllowedWeekday,
        isNotClosed,
        isValidDeliveryDate: isAllowedWeekday && isNotClosed,
    }
}

export function isWithinOpeningHours(
    value: DateTime,
    deliveryDate: DateTime,
    openingHours: DeliveryTimeDay[],
): boolean {
    const limits = getOpeningLimitsForDate(deliveryDate, openingHours)
    if (!limits) return true

    const valueTime = value.toFormat("HH:mm")
    return valueTime >= limits.minTime.toFormat("HH:mm") && valueTime <= limits.maxTime.toFormat("HH:mm")
}

export function isNotInPastIfToday(value: DateTime, deliveryDate: DateTime, dateTimeFactory: DateTimeFactory): boolean {
    if (!deliveryDate) return true
    const now = dateTimeFactory.now()
    const isToday = deliveryDate.hasSame(now, "day")
    if (!isToday) return true
    return value >= now
}
